import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import DistributedFlow from './DistributedFlow';
import { useNavigate } from 'react-router-dom';
import { useThemeMode } from '../../hooks/useThemeMode';

const Hero = () => {
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 500], [0, 200]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);
  const navigate = useNavigate();
  const { isDark } = useThemeMode();

  return (
    <>
      {/* SECTION 1: Text Intro */}
      <section className="relative min-h-screen flex flex-col justify-center items-center px-6 pt-20 overflow-hidden">

        {/* Abstract Background Glow */}
        <motion.div
          style={{ y: y1, opacity }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full blur-3xl pointer-events-none"
          aria-hidden="true"
        >
          <div style={{ width: '100%', height: '100%', backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)', borderRadius: '50%' }} />
        </motion.div>

        <div className="max-w-5xl mx-auto text-center z-10 relative">

          {/* Version Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className={`flex items-center justify-center gap-2 font-mono text-xs md:text-sm tracking-widest mb-8 ${isDark ? 'text-neutral-400' : 'text-neutral-500'}`}
          >
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            PROTOCOL LIVE
          </motion.div>

          {/* Main Title */}
          <div className="mb-6 space-y-2">
            <div className="overflow-hidden">
              <motion.h1
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.4 }}
                className={`text-6xl md:text-8xl lg:text-9xl font-bold tracking-tighter ${isDark ? 'text-white' : 'text-neutral-900'}`}
                style={{ fontFamily: 'Inter, sans-serif', lineHeight: 1.05 }}
              >
                Complete Privacy,
              </motion.h1>
            </div>
            <div className="overflow-hidden">
              <motion.h1
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.55 }}
                className="text-6xl md:text-8xl lg:text-9xl font-bold tracking-tighter text-neutral-500"
                style={{ fontFamily: 'Inter, sans-serif', lineHeight: 1.05 }}
              >
                Any Chain.
              </motion.h1>
            </div>
          </div>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className={`text-lg md:text-xl max-w-2xl mx-auto leading-relaxed mb-10 ${isDark ? 'text-neutral-400' : 'text-neutral-500'}`}
          >
            Deposit on one chain, withdraw on another with mathematical anonymity. Cross-chain privacy for EVM and Solana.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.0 }}
            className="flex flex-col sm:flex-row justify-center items-center gap-4"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/deposit')}
              className={`px-8 py-4 font-semibold rounded-sm transition-colors flex items-center gap-2 w-full sm:w-auto justify-center ${
                isDark ? 'bg-white text-black hover:bg-neutral-200' : 'bg-neutral-900 text-white hover:bg-neutral-700'
              }`}
            >
              Enter Dashboard <ArrowUpRight size={18} />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/docs')}
              className={`px-8 py-4 border font-semibold rounded-sm transition-colors w-full sm:w-auto justify-center ${
                isDark ? 'border-neutral-700 text-white' : 'border-neutral-300 text-neutral-900'
              }`}
              style={{ backgroundColor: 'transparent' }}
            >
              Read Documentation
            </motion.button>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 1 }}
          className={`absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 ${isDark ? 'text-neutral-600' : 'text-neutral-400'}`}
        >
          <span className="text-[10px] uppercase tracking-widest">Scroll to Explore</span>
          <div className={`w-px h-8 bg-gradient-to-b ${isDark ? 'from-neutral-600' : 'from-neutral-400'} to-transparent`} />
        </motion.div>
      </section>

      {/* SECTION 2: Distributed Flow Animation */}
      <section
        id="protocol"
        className={`relative border-t ${isDark ? 'border-neutral-900/50' : 'border-neutral-200/50'}`}
        style={{ background: isDark ? 'linear-gradient(to bottom, #050505, #0a0a0a)' : 'linear-gradient(to bottom, #fafafa, #f5f5f5)' }}
      >
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
        >
          <DistributedFlow />
        </motion.div>
      </section>
    </>
  );
};

export default Hero;
